import { useState, useMemo } from 'react'
import { usePlayerStore, useAuthStore } from '../stores'
import {
  Users,
  Target,
  TrendingUp,
  Plus,
  Activity,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  BarChart3,
  PieChart,
  ShieldCheck,
  CalendarDays,
  Upload,
} from 'lucide-react'
import PlayerDetail from './PlayerDetail'
import ErrorBoundary from './ErrorBoundary'
import LogPlayer from './LogPlayer'
import EditPlayer from './EditPlayer'
import PlayerCard from './PlayerCard'
import CSVImporter from './CSVImporter'
import ClubStaffManagement from './ClubStaffManagement'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  PieChart as RePieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'
import { resolveTeamCategory } from '../utils/teamCategory'
import { collectCompetitionNames } from '../utils/competitions'
import { Competition } from '../types'

type ClubTab = 'overview' | 'u13' | 'u15' | 'u17' | 'u19' | 'first-team' | 'squad' | 'competitions' | 'staff' | 'add' | 'bulk-upload'

const CATEGORY_ORDER = ['U13', 'U15', 'U17', 'U19', 'First Team'] as const

const CHART_COLORS = ['#7cff01', '#60a5fa', '#f59e0b', '#a855f7', '#ef4444']

function categoryTabToLabel(tab: ClubTab) {
  if (tab === 'first-team') return 'First Team'
  if (tab === 'squad') return 'All Squad'
  return tab.toUpperCase()
}

interface ClubDashboardProps {
  clubId?: string | null
}

function ClubDashboard({ clubId }: ClubDashboardProps) {
  const { players, clubs, competitions, competitionFixtures, addCompetition, updateCompetition, deleteCompetition } = usePlayerStore()
  const { user } = useAuthStore()

  const [activeTab, setActiveTab] = useState<ClubTab>('overview')
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null)
  const [editingPlayer, setEditingPlayer] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'age' | 'goals' | 'assists'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [competitionForm, setCompetitionForm] = useState({
    id: '',
    name: '',
    season: '',
    country: 'Nigeria',
    level: '',
    is_active: true,
  })
  const [editingCompetitionId, setEditingCompetitionId] = useState<string | null>(null)
  const [savingCompetition, setSavingCompetition] = useState(false)
  const [competitionMessage, setCompetitionMessage] = useState('')

  const resetCompetitionForm = () => {
    setEditingCompetitionId(null)
    setCompetitionForm({
      id: '',
      name: '',
      season: '',
      country: 'Nigeria',
      level: '',
      is_active: true,
    })
  }

  const resolvedClub = useMemo(() => {
    if (clubId) return clubs.find((club) => club.id === clubId) || null
    if (user?.role === 'club') {
      return clubs.find((club) =>
        (club as any).scout_id === user.id ||
        club.id === user.id ||
        club.name?.toLowerCase() === user.name?.toLowerCase()
      ) || null
    }
    return null
  }, [clubId, clubs, user?.id, user?.role, user?.name])

  const clubCompetitionNames = useMemo(
    () => collectCompetitionNames({ competitions, clubs: resolvedClub ? [resolvedClub] : [], player: null }),
    [clubs, competitions, resolvedClub]
  )
  const filteredCompetitionFixtures = useMemo(() => {
    const targetCompetitions = new Set(clubCompetitionNames)
    if (targetCompetitions.size === 0) return competitionFixtures
    return competitionFixtures.filter((fixture) => {
      const fixtureCompetition = (fixture.competition_name || (fixture as any).competition || '').trim()
      return targetCompetitions.has(fixtureCompetition)
    })
  }, [clubCompetitionNames, competitionFixtures])
  const [competitionFilter, setCompetitionFilter] = useState('All competitions')

  const clubSquad = useMemo(() => {
    if (!resolvedClub) return []
    const targetName = resolvedClub.name.trim().toLowerCase()
    return players.filter((p) => {
      if (p.owner_club_id === resolvedClub.id) return true
      return (p.club || '').trim().toLowerCase() === targetName
    })
  }, [players, resolvedClub])

  const filteredSquad = useMemo(() => {
    const term = filter.toLowerCase()
    let filtered = clubSquad.filter((p) =>
      p.name.toLowerCase().includes(term) ||
      (p.team_category || '').toLowerCase().includes(term) ||
      resolveTeamCategory(p.team_category, p.age).toLowerCase().includes(term)
    )
    // Sorting
    filtered = [...filtered].sort((a, b) => {
      let aVal: any, bVal: any
      switch (sortBy) {
        case 'age':
          aVal = a.age; bVal = b.age; break
        case 'goals':
          aVal = a.goals || 0; bVal = b.goals || 0; break
        case 'assists':
          aVal = a.assists || 0; bVal = b.assists || 0; break
        default:
          aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); break
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
    return filtered
  }, [clubSquad, filter, sortBy, sortOrder])

  const categoryPlayers = useMemo(() => {
    return CATEGORY_ORDER.reduce((acc, category) => {
      acc[category] = clubSquad.filter((p) => resolveTeamCategory(p.team_category, p.age) === category)
      return acc
    }, {} as Record<(typeof CATEGORY_ORDER)[number], typeof clubSquad>)
  }, [clubSquad])

  const categoryStats = useMemo(() => {
    return CATEGORY_ORDER.map((category, index) => {
      const playersInCategory = categoryPlayers[category]
      const count = playersInCategory.length
      const goals = playersInCategory.reduce((sum, p) => sum + (p.goals || 0), 0)
      const assists = playersInCategory.reduce((sum, p) => sum + (p.assists || 0), 0)
      const avgAge = count
        ? Math.round(playersInCategory.reduce((sum, p) => sum + p.age, 0) / count)
        : 0

      return {
        category,
        count,
        goals,
        assists,
        avgAge,
        color: CHART_COLORS[index % CHART_COLORS.length],
      }
    })
  }, [categoryPlayers])

  const positionBreakdown = useMemo(() => {
    const counts = clubSquad.reduce((acc, p) => {
      const pos = p.main_position || (p as any).pos || 'N/A'
      acc[pos] = (acc[pos] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value], index) => ({
        name,
        value,
        fill: CHART_COLORS[index % CHART_COLORS.length],
      }))
  }, [clubSquad])

  const recentPlayers = useMemo(() => {
    return [...clubSquad]
      .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
      .slice(0, 5)
  }, [clubSquad])

  if (selectedPlayer) {
    const player = players.find((p) => p.id === selectedPlayer)
    if (player) {
      return (
        <ErrorBoundary>
          <PlayerDetail
            player={player}
            onEdit={() => {
              setEditingPlayer(selectedPlayer)
              setSelectedPlayer(null)
            }}
            onBack={() => setSelectedPlayer(null)}
          />
        </ErrorBoundary>
      )
    }
  }

  if (editingPlayer) {
    const player = players.find((p) => p.id === editingPlayer)
    if (player) {
      return (
        <div className="animate-in fade-in duration-300">
          <button
            onClick={() => setEditingPlayer(null)}
            className="text-gray-500 hover:text-white font-bold text-sm tracking-widest uppercase transition mb-4 block"
          >
            ← Back to Squad
          </button>
          <EditPlayer
            player={player}
            onSave={() => {
              setEditingPlayer(null)
              setSelectedPlayer(editingPlayer)
            }}
            onCancel={() => setEditingPlayer(null)}
          />
        </div>
      )
    }
  }

  if (!resolvedClub) {
    return (
      <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-8 text-center">
        <h2 className="text-2xl font-bold text-white mb-2">Club Dashboard</h2>
        <p className="text-gray-400">Select a club from Manage Teams to view its dashboard.</p>
      </div>
    )
  }

  const renderOverview = () => {
    const totalPlayers = clubSquad.length
    const avgAge = totalPlayers
      ? Math.round(clubSquad.reduce((sum, p) => sum + p.age, 0) / totalPlayers)
      : 0
    const totalGoals = clubSquad.reduce((sum, p) => sum + (p.goals || 0), 0)
    const totalAssists = clubSquad.reduce((sum, p) => sum + (p.assists || 0), 0)
    const activePlayers = clubSquad.filter((p) => p.status === 'Active' || !p.status).length

    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          {[
            { label: 'Squad Size', value: totalPlayers, icon: Users, color: 'text-blue-500' },
            { label: 'Avg Age', value: avgAge, icon: Activity, color: 'text-orange-500' },
            { label: 'Active', value: activePlayers, icon: Target, color: 'text-green-500' },
            { label: 'Goals', value: totalGoals, icon: TrendingUp, color: 'text-purple-500' },
            { label: 'Assists', value: totalAssists, icon: ShieldCheck, color: 'text-cyan-500' },
          ].map((item) => (
            <div key={item.label} className="bg-[#161b22] border border-gray-800 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-gray-400 text-sm font-bold tracking-wider uppercase">{item.label}</h3>
                <item.icon size={20} className={item.color} />
              </div>
              <p className="text-4xl font-black text-white">{item.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6 xl:col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 size={18} className="text-green-500" />
              <h3 className="text-xl font-bold text-white">Academy Breakdown</h3>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={categoryStats} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#24303f" />
                  <XAxis dataKey="category" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#021a1a',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '12px',
                      color: '#fff',
                    }}
                  />
                  <Bar dataKey="count" fill="#7cff01" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <PieChart size={18} className="text-blue-400" />
              <h3 className="text-xl font-bold text-white">Position Mix</h3>
            </div>
            <div className="h-80">
              {positionBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <RePieChart>
                    <Pie
                      data={positionBreakdown}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={3}
                    >
                      {positionBreakdown.map((entry, index) => (
                        <Cell key={`cell-${entry.name}`} fill={entry.fill || CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#021a1a',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '12px',
                        color: '#fff',
                      }}
                    />
                    <Legend />
                  </RePieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">
                  No squad data yet
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6 xl:col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <CalendarDays size={18} className="text-cyan-400" />
              <h3 className="text-xl font-bold text-white">Development Path</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
              {categoryStats.map((item) => (
                <button
                  key={item.category}
                  onClick={() =>
                    setActiveTab(
                      item.category === 'First Team'
                        ? 'first-team'
                        : item.category === 'U13'
                          ? 'u13'
                          : item.category === 'U15'
                            ? 'u15'
                            : item.category === 'U17'
                              ? 'u17'
                              : 'u19'
                    )
                  }
                  className="text-left bg-[#0d1117] border border-gray-800 rounded-2xl p-4 hover:border-green-500/30 transition"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-black uppercase tracking-widest text-gray-400">{item.category}</span>
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  </div>
                  <p className="text-3xl font-black text-white">{item.count}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-2">
                    Avg Age {item.avgAge || 'N/A'}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
            <h3 className="text-xl font-bold mb-4 text-white">Recent Players</h3>
            {recentPlayers.length === 0 ? (
              <p className="text-gray-400">No players yet</p>
            ) : (
              <div className="space-y-3">
                {recentPlayers.map((player) => (
                  <button
                    key={player.id}
                    onClick={() => setSelectedPlayer(player.id)}
                    className="w-full text-left p-3 bg-[#0d1117] rounded-xl border border-gray-800 hover:border-green-500/30 transition"
                  >
                    <p className="text-white font-semibold truncate">{player.name}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest">
                      {resolveTeamCategory(player.team_category, player.age)} • {player.main_position || 'N/A'}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderCompetitions = () => {
    const canManageCompetitions = user?.role === 'club' || user?.role === 'admin'
    const availableCompetitions = clubCompetitionNames.length > 0
      ? clubCompetitionNames
      : Array.from(new Set(filteredCompetitionFixtures.map((fixture) => (fixture.competition_name || (fixture as any).competition || '').trim()).filter(Boolean)))
    const competitionChoices = ['All competitions', ...availableCompetitions]
    const visibleFixtures = filteredCompetitionFixtures.filter((fixture) => {
      if (competitionFilter === 'All competitions') return true
      const fixtureCompetition = (fixture.competition_name || (fixture as any).competition || '').trim()
      return fixtureCompetition === competitionFilter
    })
    const upcomingFixtures = visibleFixtures.filter((fixture) => fixture.status === 'upcoming' || fixture.status === 'live')
    const recentResults = visibleFixtures.filter((fixture) => fixture.status === 'played')
    const totalGoals = recentResults.reduce((sum, fixture) => sum + (fixture.home_score || 0) + (fixture.away_score || 0), 0)
    const homeGoals = recentResults.reduce((sum, fixture) => sum + (fixture.home_score || 0), 0)
    const awayGoals = recentResults.reduce((sum, fixture) => sum + (fixture.away_score || 0), 0)
    const averageGoals = recentResults.length ? (totalGoals / recentResults.length).toFixed(1) : '0.0'
    const latestAnalysis = recentResults.find((fixture) => fixture.analysis)?.analysis
    const standings = (() => {
      const table = new Map<string, { team: string; played: number; won: number; drawn: number; lost: number; gf: number; ga: number; points: number }>()
      const ensureTeam = (team: string) => {
        if (!table.has(team)) {
          table.set(team, { team, played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0, points: 0 })
        }
        return table.get(team)!
      }

      recentResults.forEach((fixture) => {
        const home = ensureTeam(fixture.home_team)
        const away = ensureTeam(fixture.away_team)
        const homeScore = fixture.home_score ?? 0
        const awayScore = fixture.away_score ?? 0

        home.played += 1
        away.played += 1
        home.gf += homeScore
        home.ga += awayScore
        away.gf += awayScore
        away.ga += homeScore

        if (homeScore > awayScore) {
          home.won += 1
          away.lost += 1
          home.points += 3
        } else if (homeScore < awayScore) {
          away.won += 1
          home.lost += 1
          away.points += 3
        } else {
          home.drawn += 1
          away.drawn += 1
          home.points += 1
          away.points += 1
        }
      })

      return Array.from(table.values()).sort((a, b) => {
        const goalDiffA = a.gf - a.ga
        const goalDiffB = b.gf - b.ga
        return b.points - a.points || goalDiffB - goalDiffA || b.gf - a.gf || a.team.localeCompare(b.team)
      })
    })()

    const handleCompetitionSave = async () => {
      if (!competitionForm.name.trim()) return
      setSavingCompetition(true)
      setCompetitionMessage('')
      try {
        const payload: Competition = {
          id: editingCompetitionId || crypto.randomUUID(),
          name: competitionForm.name.trim(),
          season: competitionForm.season.trim() || undefined,
          country: competitionForm.country.trim() || undefined,
          level: competitionForm.level.trim() || undefined,
          is_active: competitionForm.is_active,
          created_by: user?.id,
        }
        if (editingCompetitionId) {
          await updateCompetition(editingCompetitionId, payload)
          setCompetitionMessage('Competition updated and saved to Supabase.')
        } else {
          await addCompetition(payload)
          setCompetitionMessage('Competition saved to Supabase and available in dropdowns.')
        }
        resetCompetitionForm()
      } catch (error) {
        setCompetitionMessage(error instanceof Error ? error.message : 'Failed to save competition')
      } finally {
        setSavingCompetition(false)
      }
    }

    const handleEditCompetition = (competition: Competition) => {
      setEditingCompetitionId(competition.id)
      setCompetitionForm({
        id: competition.id,
        name: competition.name,
        season: competition.season || '',
        country: competition.country || 'Nigeria',
        level: competition.level || '',
        is_active: competition.is_active ?? true,
      })
      setCompetitionMessage('')
    }

    const handleDeleteCompetition = async (competition: Competition) => {
      if (!window.confirm(`Delete ${competition.name}? This removes it from dropdowns.`)) return
      setSavingCompetition(true)
      setCompetitionMessage('')
      try {
        await deleteCompetition(competition.id)
        if (editingCompetitionId === competition.id) {
          resetCompetitionForm()
        }
        setCompetitionMessage('Competition deleted.')
      } catch (error) {
        setCompetitionMessage(error instanceof Error ? error.message : 'Failed to delete competition')
      } finally {
        setSavingCompetition(false)
      }
    }

    const handleToggleCompetitionStatus = async (competition: Competition) => {
      setSavingCompetition(true)
      setCompetitionMessage('')
      try {
        await updateCompetition(competition.id, {
          is_active: !competition.is_active,
        })
        setCompetitionMessage(`${competition.name} is now ${competition.is_active ? 'inactive' : 'active'}.`)
      } catch (error) {
        setCompetitionMessage(error instanceof Error ? error.message : 'Failed to update competition status')
      } finally {
        setSavingCompetition(false)
      }
    }

    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        {canManageCompetitions && (
          <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Competition Setup</p>
                <h4 className="text-2xl font-black text-white">Add a new competition</h4>
                <p className="text-gray-500 text-sm">This will be saved in Supabase and appear in dropdowns across the app.</p>
              </div>
            </div>
            {editingCompetitionId && (
              <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/10 px-4 py-3 text-sm text-yellow-200">
                Editing {competitionForm.name || 'competition'}.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                value={competitionForm.name}
                onChange={(e) => setCompetitionForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Competition name"
                className="px-4 py-3 rounded-2xl bg-[#0d1117] border border-gray-700 text-white placeholder-gray-500 outline-none focus:border-green-500"
              />
              <input
                value={competitionForm.season}
                onChange={(e) => setCompetitionForm((prev) => ({ ...prev, season: e.target.value }))}
                placeholder="Season, e.g. 2025/26"
                className="px-4 py-3 rounded-2xl bg-[#0d1117] border border-gray-700 text-white placeholder-gray-500 outline-none focus:border-green-500"
              />
              <input
                value={competitionForm.country}
                onChange={(e) => setCompetitionForm((prev) => ({ ...prev, country: e.target.value }))}
                placeholder="Country"
                className="px-4 py-3 rounded-2xl bg-[#0d1117] border border-gray-700 text-white placeholder-gray-500 outline-none focus:border-green-500"
              />
              <input
                value={competitionForm.level}
                onChange={(e) => setCompetitionForm((prev) => ({ ...prev, level: e.target.value }))}
                placeholder="Level, e.g. Top Flight"
                className="px-4 py-3 rounded-2xl bg-[#0d1117] border border-gray-700 text-white placeholder-gray-500 outline-none focus:border-green-500"
              />
            </div>
            <label className="flex items-center gap-3 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={competitionForm.is_active}
                onChange={(e) => setCompetitionForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-700 bg-gray-900 text-green-500 focus:ring-green-500"
              />
              Active competition
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleCompetitionSave}
                disabled={savingCompetition}
                className="rounded-xl bg-green-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-green-500 disabled:opacity-50"
              >
                {savingCompetition ? 'Saving...' : editingCompetitionId ? 'Update Competition' : 'Save Competition'}
              </button>
              {editingCompetitionId && (
                <button
                  type="button"
                  onClick={resetCompetitionForm}
                  className="rounded-xl bg-gray-800 px-5 py-3 text-sm font-semibold text-white transition hover:bg-gray-700"
                >
                  Cancel Edit
                </button>
              )}
              {competitionMessage && (
                <p className="text-sm text-gray-400">{competitionMessage}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {competitions.map((competition) => (
                <div key={competition.id} className="rounded-2xl border border-gray-800 bg-[#0d1117] p-4 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-black text-white truncate">{competition.name}</p>
                    <p className="text-xs text-gray-500">
                      {competition.season || 'Season N/A'} • {competition.country || 'Country N/A'} • {competition.level || 'Level N/A'}
                    </p>
                    <span className={`inline-flex mt-2 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                      competition.is_active
                        ? 'border-green-500/30 bg-green-500/10 text-green-400'
                        : 'border-gray-700 bg-gray-800 text-gray-400'
                    }`}>
                      {competition.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleToggleCompetitionStatus(competition)}
                      className="rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-xs font-semibold text-gray-200 hover:text-white flex items-center gap-2"
                      title="Toggle active status"
                    >
                      {competition.is_active ? <ToggleLeft size={16} /> : <ToggleRight size={16} />}
                      {competition.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEditCompetition(competition)}
                      className="rounded-lg border border-gray-700 bg-gray-900 p-2 text-gray-300 hover:text-white"
                      title="Edit competition"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteCompetition(competition)}
                      className="rounded-lg border border-red-900/40 bg-red-950/30 p-2 text-red-400 hover:text-red-300"
                      title="Delete competition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Competition Center</p>
              <h3 className="text-2xl font-black text-white">Fixtures, Results & Match Notes</h3>
              <p className="text-gray-500 text-sm">A live-livescore style board for the club's tracked competitions.</p>
            </div>
            <select
              value={competitionFilter}
              onChange={(e) => setCompetitionFilter(e.target.value)}
              className="px-4 py-3 rounded-2xl bg-[#0d1117] border border-gray-700 text-white text-sm font-bold"
            >
              {competitionChoices.map((competition) => (
                <option key={competition} value={competition}>{competition}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          {[
            { label: 'Tracked Competitions', value: availableCompetitions.length },
            { label: 'Fixtures', value: visibleFixtures.length },
            { label: 'Live / Upcoming', value: upcomingFixtures.length },
            { label: 'Results', value: recentResults.length },
          ].map((item) => (
            <div key={item.label} className="bg-[#161b22] border border-gray-800 rounded-2xl p-5">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">{item.label}</p>
              <p className="text-3xl font-black text-white">{item.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6 xl:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">Fixtures & Results</p>
                <h4 className="text-xl font-black text-white">Live board</h4>
              </div>
              <span className="text-xs font-black uppercase tracking-widest text-gray-500">{competitionFilter}</span>
            </div>

            {visibleFixtures.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-800 bg-[#0d1117] p-8 text-center text-gray-500">
                No fixtures found for this competition.
              </div>
            ) : (
              <div className="space-y-4">
                {visibleFixtures.map((fixture) => {
                  const fixtureCompetition = fixture.competition_name || (fixture as any).competition || 'Competition'
                  const scoreline = fixture.status === 'played'
                    ? `${fixture.home_score ?? 0} - ${fixture.away_score ?? 0}`
                    : fixture.status === 'live'
                      ? 'LIVE'
                      : 'VS'
                  return (
                    <div key={fixture.id} className="rounded-3xl border border-gray-800 bg-[#0d1117] p-5">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">
                            {fixtureCompetition} {fixture.matchday ? `• ${fixture.matchday}` : ''}
                          </p>
                          <p className="text-sm text-gray-400">{fixture.fixture_date} {fixture.kickoff_time ? `• ${fixture.kickoff_time}` : ''} {fixture.venue ? `• ${fixture.venue}` : ''}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                          fixture.status === 'live'
                            ? 'border-red-500/30 bg-red-500/10 text-red-400'
                            : fixture.status === 'played'
                              ? 'border-green-500/30 bg-green-500/10 text-green-400'
                              : fixture.status === 'postponed'
                                ? 'border-yellow-500/30 bg-yellow-500/10 text-yellow-300'
                                : 'border-blue-500/30 bg-blue-500/10 text-blue-300'
                        }`}>
                          {fixture.status}
                        </span>
                      </div>

                      <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                        <div className="text-right">
                          <p className="text-white font-black text-lg truncate">{fixture.home_team}</p>
                          <p className="text-[10px] text-gray-500 uppercase tracking-widest">Home</p>
                        </div>
                        <div className="text-center min-w-[84px]">
                          <p className="text-2xl font-black text-[#7cff01]">{scoreline}</p>
                          {fixture.status !== 'played' && (
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest">Preview</p>
                          )}
                        </div>
                        <div>
                          <p className="text-white font-black text-lg truncate">{fixture.away_team}</p>
                          <p className="text-[10px] text-gray-500 uppercase tracking-widest">Away</p>
                        </div>
                      </div>

                      {fixture.analysis && (
                        <p className="mt-4 text-sm text-gray-400 leading-relaxed">{fixture.analysis}</p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
              <h4 className="text-xl font-black text-white mb-4">Match Analysis</h4>
              <div className="space-y-4">
                <div className="rounded-2xl bg-[#0d1117] border border-gray-800 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Avg Goals per Match</p>
                  <p className="text-3xl font-black text-[#7cff01]">{averageGoals}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl bg-[#0d1117] border border-gray-800 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Home Goals</p>
                    <p className="text-2xl font-black text-white">{homeGoals}</p>
                  </div>
                  <div className="rounded-2xl bg-[#0d1117] border border-gray-800 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Away Goals</p>
                    <p className="text-2xl font-black text-white">{awayGoals}</p>
                  </div>
                </div>
                <div className="rounded-2xl bg-[#0d1117] border border-gray-800 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">Latest Note</p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {latestAnalysis || 'No result analysis added yet. As results are recorded, this panel highlights key patterns and takeaways.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
              <h4 className="text-xl font-black text-white mb-4">Recent Results</h4>
              {recentResults.length === 0 ? (
                <p className="text-gray-500 text-sm">No completed results yet.</p>
              ) : (
                <div className="space-y-3">
                  {recentResults.slice(0, 4).map((fixture) => (
                    <div key={fixture.id} className="rounded-2xl border border-gray-800 bg-[#0d1117] p-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">{fixture.fixture_date}</p>
                      <p className="text-white font-black">{fixture.home_team} {fixture.home_score ?? 0} - {fixture.away_score ?? 0} {fixture.away_team}</p>
                      <p className="text-xs text-gray-500 mt-1">{fixture.competition_name || (fixture as any).competition || 'Competition'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
              <h4 className="text-xl font-black text-white mb-4">Standings</h4>
              {standings.length === 0 ? (
                <p className="text-gray-500 text-sm">No completed fixtures to calculate standings yet.</p>
              ) : (
                <div className="space-y-2">
                  {standings.slice(0, 8).map((team, index) => {
                    const goalDiff = team.gf - team.ga
                    return (
                      <div key={team.team} className="grid grid-cols-[24px_1fr_auto] items-center gap-3 rounded-2xl border border-gray-800 bg-[#0d1117] px-3 py-3">
                        <span className="text-xs font-black text-gray-500">{index + 1}</span>
                        <div className="min-w-0">
                          <p className="text-white font-semibold truncate">{team.team}</p>
                          <p className="text-[10px] text-gray-500 uppercase tracking-widest">
                            P{team.played} W{team.won} D{team.drawn} L{team.lost}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-black">{team.points} pts</p>
                          <p className="text-[10px] text-gray-500 uppercase tracking-widest">
                            GD {goalDiff >= 0 ? '+' : ''}{goalDiff}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderCategory = (category: Exclude<ClubTab, 'overview' | 'squad' | 'add'>) => {
    const label = categoryTabToLabel(category)
    let playersInCategory =
      category === 'u13' ? categoryPlayers.U13 :
      category === 'u15' ? categoryPlayers.U15 :
      category === 'u17' ? categoryPlayers.U17 :
      category === 'u19' ? categoryPlayers.U19 :
      categoryPlayers['First Team']
    // Sorting
    playersInCategory = [...playersInCategory].sort((a, b) => {
      let aVal: any, bVal: any
      switch (sortBy) {
        case 'age':
          aVal = a.age; bVal = b.age; break
        case 'goals':
          aVal = a.goals || 0; bVal = b.goals || 0; break
        case 'assists':
          aVal = a.assists || 0; bVal = b.assists || 0; break
        default:
          aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); break
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">{label} Section</p>
              <h3 className="text-2xl font-black text-white">{playersInCategory.length} players</h3>
              <p className="text-gray-500 text-sm">Manage academy talent in the {label} group.</p>
            </div>
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as any)}
                className="px-3 py-2 rounded-lg bg-[#0d1117] border border-gray-700 text-white text-sm font-bold"
              >
                <option value="name">Sort: Name</option>
                <option value="age">Sort: Age</option>
                <option value="goals">Sort: Goals</option>
                <option value="assists">Sort: Assists</option>
              </select>
              <select
                value={sortOrder}
                onChange={e => setSortOrder(e.target.value as any)}
                className="px-3 py-2 rounded-lg bg-[#0d1117] border border-gray-700 text-white text-sm font-bold"
              >
                <option value="asc">Asc</option>
                <option value="desc">Desc</option>
              </select>
              <button
                onClick={() => setActiveTab('add')}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition"
              >
                <Plus size={18} />
                Add Player
              </button>
            </div>
          </div>
        </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-5 lg:gap-6 px-1 sm:px-0">
           {playersInCategory.length === 0 ? (
            <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-12 text-center lg:col-span-2">
              <p className="text-gray-400 mb-4">No players in this section yet.</p>
              <button
                onClick={() => setActiveTab('add')}
                className="text-green-500 hover:text-green-400 font-bold underline"
              >
                Add your first player
              </button>
            </div>
          ) : (
            playersInCategory.map((player) => (
              <PlayerCard
                key={player.id}
                player={player}
                onViewProfile={(id) => setSelectedPlayer(id)}
                adminActions={
                  <div className="flex gap-2">
                    <span className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full border ${
                      player.status === 'Active' ? 'bg-green-900/10 border-green-800/30 text-green-400' :
                      player.status === 'Injured' ? 'bg-red-900/10 border-red-800/30 text-red-400' :
                      'bg-gray-800 border-gray-700 text-gray-500'
                    }`}>
                      {player.status || 'Active'}
                    </span>
                    <button
                      onClick={() => setEditingPlayer(player.id)}
                      className="p-2 bg-gray-900 border border-gray-800 rounded-xl text-gray-400 hover:text-white transition-colors"
                      title="Edit Player"
                    >
                      <Edit size={18} />
                    </button>
                  </div>
                }
              />
            ))
          )}
        </div>
      </div>
    )
  }

  const renderSquad = () => (
    <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-300">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-[#161b22] p-4 rounded-2xl border border-gray-800">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search squad by name or team category (e.g. U-19)..."
          className="w-full md:w-96 px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:outline-none"
        />
        <div className="flex gap-2 items-center">
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value as any)}
            className="px-3 py-2 rounded-lg bg-[#0d1117] border border-gray-700 text-white text-sm font-bold"
          >
            <option value="name">Sort: Name</option>
            <option value="age">Sort: Age</option>
            <option value="goals">Sort: Goals</option>
            <option value="assists">Sort: Assists</option>
          </select>
          <select
            value={sortOrder}
            onChange={e => setSortOrder(e.target.value as any)}
            className="px-3 py-2 rounded-lg bg-[#0d1117] border border-gray-700 text-white text-sm font-bold"
          >
            <option value="asc">Asc</option>
            <option value="desc">Desc</option>
          </select>
          <button
            onClick={() => setActiveTab('add')}
            className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition"
          >
            <Plus size={20} />
            Add Player
          </button>
        </div>
      </div>

      {filteredSquad.length === 0 ? (
        <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-12 text-center">
          <p className="text-gray-400 mb-4">Your squad is empty or no players match your search.</p>
          <button
            onClick={() => setActiveTab('add')}
            className="text-green-500 hover:text-green-400 font-bold underline"
          >
            Add your first player
          </button>
        </div>
      ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-5 lg:gap-6 px-1 sm:px-0">
           {filteredSquad.map((player) => (
            <PlayerCard
              key={player.id}
              player={player}
              onViewProfile={(id) => setSelectedPlayer(id)}
              adminActions={
                <div className="flex gap-2">
                  <span className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full border ${
                    player.status === 'Active' ? 'bg-green-900/10 border-green-800/30 text-green-400' :
                    player.status === 'Injured' ? 'bg-red-900/10 border-red-800/30 text-red-400' :
                    'bg-gray-800 border-gray-700 text-gray-500'
                  }`}>
                    {player.status || 'Active'}
                  </span>
                  <button
                    onClick={() => setEditingPlayer(player.id)}
                    className="p-2 bg-gray-900 border border-gray-800 rounded-xl text-gray-400 hover:text-white transition-colors"
                    title="Edit Player"
                  >
                    <Edit size={18} />
                  </button>
                </div>
              }
            />
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 border-b border-gray-800 pb-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-3xl font-display font-black text-white">{resolvedClub.name} Headquarters</h2>
          <p className="text-sm text-gray-500 mt-1">Academy oversight, age bands, and player development data.</p>
        </div>

        <div className="flex flex-wrap gap-2 bg-[#161b22] p-1 rounded-xl border border-gray-800">
          {[
            { id: 'overview', label: 'Dashboard' },
            { id: 'u13', label: 'U13' },
            { id: 'u15', label: 'U15' },
            { id: 'u17', label: 'U17' },
            { id: 'u19', label: 'U19' },
            { id: 'first-team', label: 'First Team' },
            { id: 'squad', label: 'Squad' },
            { id: 'competitions', label: 'Competitions' },
            { id: 'staff', label: 'Staff' },
            { id: 'bulk-upload', label: 'Bulk Upload' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as ClubTab)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition ${
                activeTab === tab.id
                  ? tab.id === 'bulk-upload'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-800 text-white'
                  : tab.id === 'bulk-upload'
                    ? 'text-green-500 hover:text-green-400 flex items-center gap-1'
                    : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {tab.id === 'bulk-upload' && activeTab !== 'bulk-upload' && <Upload size={13} className="inline mr-1" />}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'u13' && renderCategory('u13')}
      {activeTab === 'u15' && renderCategory('u15')}
      {activeTab === 'u17' && renderCategory('u17')}
      {activeTab === 'u19' && renderCategory('u19')}
      {activeTab === 'first-team' && renderCategory('first-team')}
      {activeTab === 'squad' && renderSquad()}
      {activeTab === 'competitions' && renderCompetitions()}
      {activeTab === 'staff' && resolvedClub && (
        <div className="animate-in fade-in duration-300">
          <ClubStaffManagement
            clubId={resolvedClub.id}
            clubName={resolvedClub.name}
          />
        </div>
      )}
      {activeTab === 'add' && (
        <div className="animate-in slide-in-from-right-8 duration-300">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => setActiveTab('squad')}
              className="text-gray-500 hover:text-white font-bold text-sm tracking-widest uppercase transition"
            >
              ← Back to Squad
            </button>
          </div>
           <LogPlayer isClubMode={true} onSuccess={() => setActiveTab('squad')} clubId={resolvedClub?.id} clubName={resolvedClub?.name} />
        </div>
      )}
      {activeTab === 'bulk-upload' && (
        <div className="animate-in fade-in duration-300">
          <CSVImporter />
        </div>
      )}
    </div>
  )
}

export default ClubDashboard
